import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  PackageCheck, Send, Clock, AlertTriangle, CheckCircle2, Loader2, RefreshCw,
  ChevronLeft, FileText, Link2, Copy, Truck,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { LogContact, ReturnThread } from './ReturnsConversation';
import { takeDeepLink } from './deepLink';
import { CHANNEL_LABEL, toneStyle } from './returnsLoopTokens';

const API = '/api';

const STATUS_META = {
  awaiting_send: { label: 'Awaiting send', cls: 'text-amber-300 border-amber-400/40 bg-amber-400/10' },
  in_progress: { label: 'In progress', cls: 'text-blue-300 border-blue-400/40 bg-blue-400/10' },
  overdue:     { label: 'Overdue',     cls: 'text-red-400 border-red-500/40 bg-red-500/10' },
  received:    { label: 'Received',    cls: 'text-hg-green border-hg-green/40 bg-hg-green/10' },
  lost:        { label: 'Lost',        cls: 'text-hg-text-dim border-hg-border bg-hg-bg' },
};

const FILTERS = ['awaiting_send', 'in_progress', 'overdue', 'received', 'lost', 'all'];

// Mirrors returns_model.CLOSED_STATUSES: a closed return offers no more actions.
const CLOSED = ['completed', 'received', 'cancelled', 'lost'];
const isClosed = (ret) => CLOSED.includes(ret?.status);

const fmtDate = (s) => { try { return s ? new Date(s).toLocaleString() : '—'; } catch { return s; } };

const money = (v) => (v === null || v === undefined ? '$3,500' : `$${Number(v).toLocaleString()}`);

// --- module-level subcomponents (stable types; avoid remount/focus loss) ---

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.in_progress;
  return <span className={`text-[10px] font-sans uppercase tracking-wider rounded-sm px-1.5 py-0.5 border ${m.cls}`}>{m.label}</span>;
}

function ChargeChip({ amount }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-sans rounded-sm px-1.5 py-0.5 border text-red-400 border-red-500/40 bg-red-500/10">
      <AlertTriangle size={10} /> {money(amount)} charge ready
    </span>
  );
}

function Field({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-hg-text-dim font-sans">{label}</div>
      <div className="text-sm font-sans text-hg-text break-words">{value ?? '—'}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-3">
      <div className="text-xs font-sans text-hg-text uppercase tracking-wider">{title}</div>
      {children}
    </div>
  );
}

const SHIP_MODES = [
  { id: 'prepaid_label', label: "We'll email a prepaid label" },
  { id: 'reimburse_up_to', label: 'Reimburse shipping up to $…' },
  { id: 'fitter_arranges', label: 'Fitter arranges + pays shipping' },
];

const TIER_HINT = {
  lower48: 'Lower 48 — a prepaid label is usually the easy call.',
  ak_hi: 'Alaska / Hawaii — shipping a full system runs FAR higher than mainland; a reimburse-up-to cap usually fits better than a flat label.',
  international: 'International — freight + customs; usually fitter-arranges or a negotiated reimbursement.',
};

// Per-return shipping deal + the fitter's helper page link.
function ShippingSection({ ret, actor, onSaved }) {
  const arr = ret.shipping_arrangement;
  const [editing, setEditing] = useState(!arr);
  const [mode, setMode] = useState(arr?.mode || '');
  const [amount, setAmount] = useState(arr?.amount ?? '');
  const [note, setNote] = useState(arr?.note ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const save = async () => {
    setSaving(true); setError('');
    try {
      const r = await axios.post(`${API}/fitter-returns/${ret.return_id}/shipping`, {
        mode, amount: amount === '' ? null : Number(amount), note: note || null, actor,
      });
      setEditing(false);
      onSaved?.(r.data);
    } catch (e) { setError(e.response?.data?.detail || 'Could not save.'); }
    setSaving(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(ret.helper_url);
      setCopied(true); setTimeout(() => setCopied(false), 2500);
    } catch { /* clipboard denied — the link is visible to select manually */ }
  };

  const hint = TIER_HINT[ret.location_tier] ||
    'Location unknown — confirm where this fitter ships from before picking the deal (an island or overseas return costs far more).';

  return (
    <Section title="Shipping deal + fitter return page">
      {!arr && !isClosed(ret) && (
        <div className="text-sm font-sans text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded-sm px-4 py-3 flex items-start gap-2" data-testid="ret-shipping-unset">
          <Truck size={15} className="mt-0.5 flex-shrink-0" />
          <span><strong>Shipping arrangement not set.</strong> Decide this fitter's deal before the
          return email or helper link goes out — the page says "we'll sort shipping out together" until then. {hint}</span>
        </div>
      )}
      {arr && (
        <div className="text-sm font-sans text-hg-text flex items-center gap-2 flex-wrap">
          <Truck size={13} className="text-hg-green" />
          {SHIP_MODES.find((m) => m.id === arr.mode)?.label || arr.mode}
          {arr.amount != null && <span className="text-hg-green">${arr.amount}</span>}
          {arr.note && <span className="text-hg-text-dim text-xs">· “{arr.note}”</span>}
          {!editing && !isClosed(ret) && (
            <button onClick={() => setEditing(true)} className="text-xs text-hg-text-dim underline hover:text-hg-text">edit</button>
          )}
        </div>
      )}
      {editing && !isClosed(ret) && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="ret-shipping-form">
          <select value={mode} onChange={(e) => setMode(e.target.value)}
            className="bg-hg-bg border border-hg-border rounded-sm px-2.5 py-1.5 text-xs font-sans text-hg-text">
            <option value="">Pick the deal…</option>
            {SHIP_MODES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
          {mode === 'reimburse_up_to' && (
            <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" min="1"
              placeholder="$ cap" className="w-24 bg-hg-bg border border-hg-border rounded-sm px-2.5 py-1.5 text-xs font-sans text-hg-text" />
          )}
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="note shown to the fitter (optional)"
            className="w-64 bg-hg-bg border border-hg-border rounded-sm px-2.5 py-1.5 text-xs font-sans text-hg-text" />
          <button onClick={save} disabled={saving || !mode || (mode === 'reimburse_up_to' && !amount)}
            className="px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40"
            data-testid="ret-shipping-save">
            {saving ? <Loader2 size={12} className="animate-spin" /> : 'Save deal'}
          </button>
          {arr && <button onClick={() => setEditing(false)} className="text-xs text-hg-text-dim hover:text-hg-text">cancel</button>}
        </div>
      )}
      {error && <div className="text-xs text-red-400 font-sans">{error}</div>}
      {ret.helper_url && (
        <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-hg-border">
          <Link2 size={12} className="text-hg-green flex-shrink-0" />
          <span className="text-[11px] font-sans text-hg-text-dim break-all flex-1 min-w-[200px]">{ret.helper_url}</span>
          <button onClick={copyLink} data-testid="ret-copy-helper-link"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-green hover:border-hg-green">
            <Copy size={11} /> {copied ? 'Copied!' : 'Copy link'}
          </button>
          {/* helper_opened_at used to render as a green "opened <date>", which read as
              "the holder opened it". It isn't: it fires once and is tripped by anything
              that loads the URL, including staff previewing from here and our own audit
              scripts. Doug's said "opened 7/27" while being timestamped 23 minutes BEFORE
              his email was sent. Genuine visits only, or an explicit "not seen". */}
          {ret.page_visits && (
            ret.page_visits.genuine_visits > 0 ? (
              <span className="text-[10px] font-sans text-hg-green" data-testid="ret-visits">
                seen {ret.page_visits.genuine_visits}×
                {' · first '}{fmtDate(ret.page_visits.first_genuine_visit)}
                {ret.page_visits.genuine_visits > 1 && <> · last {fmtDate(ret.page_visits.last_genuine_visit)}</>}
              </span>
            ) : (
              <span className="text-[10px] font-sans text-amber-300" data-testid="ret-visits"
                title={`No genuine visit recorded. Staff previews and link scanners are excluded. ${ret.page_visits.tracking_since || ''}`}>
                not opened yet
              </span>
            )
          )}
        </div>
      )}
      <p className="text-[10px] font-sans text-hg-text-dim">
        The fitter's personal page: kit checklist, ship-to address, this shipping deal in warm language,
        and a self-serve tracking form. It never shows the non-return charge. The return + reminder
        emails include this link automatically.
      </p>
    </Section>
  );
}

function overdueLabel(ret) {
  if (isClosed(ret)) return null;
  // No clock yet (awaiting_send: the 30-day window starts at actual send) — no countdown.
  if (ret.status === 'awaiting_send' || ret.days_left == null) return null;
  if (ret.is_overdue) {
    const d = Math.abs(ret.days_left ?? 0);
    return { text: `OVERDUE by ${d} day${d === 1 ? '' : 's'}`, cls: 'text-red-400' };
  }
  const d = ret.days_left;
  return { text: `${d} day${d === 1 ? '' : 's'} left`, cls: 'text-hg-text-dim' };
}

function ReturnCard({ ret, onOpen }) {
  const od = overdueLabel(ret);
  return (
    <button
      data-testid={`ret-card-${ret.return_id}`}
      onClick={() => onOpen(ret.return_id)}
      className="w-full text-left bg-hg-surface border border-hg-border rounded-sm p-4 space-y-2 hover:border-hg-green transition-colors"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-sans text-hg-text flex items-center gap-1.5">
            <PackageCheck size={13} className="text-hg-green" /> {ret.fitter_name || '(no name)'}
          </div>
          {ret.fitter_email && (
            <div className="text-[11px] font-sans text-hg-text-dim mt-0.5">{ret.fitter_email}</div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <StatusBadge status={ret.status} />
          {!ret.shipping_arrangement && !isClosed(ret) && (
            <span className="text-[10px] font-sans uppercase tracking-wider rounded-sm px-1.5 py-0.5 border text-amber-300 border-amber-400/40 bg-amber-400/10">
              no shipping deal
            </span>
          )}
          {ret.tracking?.number && !isClosed(ret) && (
            <span className="text-[10px] font-sans uppercase tracking-wider rounded-sm px-1.5 py-0.5 border text-hg-green border-hg-green/40 bg-hg-green/10">
              tracking in
            </span>
          )}
          {ret.nonreturn_charge_flagged && <ChargeChip amount={ret.nonreturn_charge_amount} />}
        </div>
      </div>
      <div className="flex items-center gap-4 flex-wrap text-[11px] font-sans text-hg-text-dim">
        <span className="inline-flex items-center gap-1"><Clock size={11} /> Return by {fmtDate(ret.return_by_date)}</span>
        {od && <span className={`· ${od.cls}`}>· {od.text}</span>}
        {ret.status === 'received' && ret.received_at && <span className="text-hg-green">· received {fmtDate(ret.received_at)}</span>}
      </div>
    </button>
  );
}

// --- start a return ---

function StartReturn({ actor, onStarted }) {
  const [fitters, setFitters] = useState([]);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/fitters`);
      setFitters(r.data?.fitters || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load fitters.');
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const start = async () => {
    if (!selected) { setError('Pick a fitter first.'); return; }
    setBusy(true); setError('');
    try {
      const r = await axios.post(`${API}/fitter-returns/start`, { fitter_id: selected, actor });
      const ret = r.data?.return || r.data;
      onStarted?.(ret?.return_id);
      setSelected('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not start the return.');
    }
    setBusy(false);
  };

  return (
    <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-3">
      <div className="text-xs font-sans text-hg-text uppercase tracking-wider">Start a return</div>
      <div className="flex gap-2 flex-wrap items-center">
        <select
          data-testid="ret-start-select"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          disabled={loading || busy}
          className="flex-1 min-w-[260px] bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green disabled:opacity-40"
        >
          <option value="">{loading ? 'Loading fitters…' : 'Select a fitter…'}</option>
          {fitters.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}{f.primary_email ? ` — ${f.primary_email}` : ''}{f.status ? ` (${f.status})` : ''}{f.is_test ? ' (TEST)' : ''}
            </option>
          ))}
        </select>
        <button
          data-testid="ret-start-btn"
          onClick={start}
          disabled={busy || loading || !selected}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40"
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <PackageCheck size={13} />} Start return process
        </button>
      </div>
      {error && <div className="text-xs text-red-400 font-sans">{error}</div>}
    </div>
  );
}

// --- detail view ---

function ReturnDetail({ id, actor, onBack, onChanged }) {
  const [ret, setRet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // return email draft state
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendOk, setSendOk] = useState('');
  const [emailError, setEmailError] = useState('');

  // reminder draft state
  const [remSubject, setRemSubject] = useState('');
  const [remBody, setRemBody] = useState('');
  const [sendingRem, setSendingRem] = useState(false);
  const [remError, setRemError] = useState('');

  // received action state
  const [receiving, setReceiving] = useState(false);
  const [receivedError, setReceivedError] = useState('');

  // tracking + billing-stop state
  const [trkNumber, setTrkNumber] = useState('');
  const [trkCarrier, setTrkCarrier] = useState('');
  const [trkError, setTrkError] = useState('');

  const onSaveTracking = async () => {
    setTrkError('');
    try {
      const r = await axios.post(`${API}/fitter-returns/${id}/tracking`, {
        tracking_number: trkNumber.trim(), carrier: trkCarrier.trim() || null, actor,
      });
      hydrate(r.data);
    } catch (e) { setTrkError(e.response?.data?.detail || 'Could not save tracking.'); }
  };

  const onBillingStopped = async () => {
    try {
      const r = await axios.post(`${API}/fitter-returns/${id}/billing-stopped`, { actor });
      hydrate(r.data);
    } catch { /* refresh reconciles */ }
  };

  const hydrate = useCallback((data) => {
    setRet(data);
    // Once the return email has gone, the editor must show the FOLLOW-UP draft, not the
    // original. email_draft is the record of what the holder already received — loading it
    // into the box would invite re-sending the same (in Doug's case, unclickable) email.
    const ed = ((data?.return_email_sent_at && data?.followup_draft)
                || data?.email_draft || {});
    setEmailSubject(ed.subject || '');
    setEmailBody(ed.body || '');
    const rd = data?.reminder_draft || {};
    setRemSubject(rd.subject || `Reminder: please return your Henry-Griffitts demo system`);
    setRemBody(rd.body || `Hi ${data?.fitter_name || 'there'},\n\nThis is a friendly reminder that your Henry-Griffitts demo system is past the 30-day return window. Please arrange to return the full kit at your earliest convenience.\n\nThank you,\nHenry-Griffitts`);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API}/fitter-returns/${id}`);
      hydrate(r.data?.return || r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load return.');
    }
    setLoading(false);
  }, [id, hydrate]);
  useEffect(() => { load(); }, [load]);

  const sendEmail = async () => {
    setSendingEmail(true); setEmailError(''); setSendOk('');
    try {
      // Once the return email has gone, a second send is a FOLLOW-UP: it gets its own
      // record and never overwrites return_email_sent_at or moves return_by_date. The old
      // path re-stamped the original send time, losing when the holder was first told.
      const path = ret.return_email_sent_at ? 'send-followup' : 'send-email';
      const r = await axios.post(`${API}/fitter-returns/${id}/${path}`, { actor, subject: emailSubject, body: emailBody });
      const doc = r.data?.return || r.data;
      const state = path === 'send-followup'
        ? doc?.followup_dispatch_state : doc?.return_email_dispatch_state;
      setSendOk(state === 'confirmed'
        ? `Sent and delivery CONFIRMED to ${doc?.fitter_email}.`
        : `Handed to MailerLite for ${doc?.fitter_email}. Delivery is not confirmed yet — `
          + `re-open this return in a few minutes and the line below will say confirmed.`);
      hydrate(r.data?.return || r.data);
      onChanged?.();
    } catch (e) {
      setEmailError(e.response?.data?.detail || 'Send failed.');
    }
    setSendingEmail(false);
  };

  const sendReminder = async () => {
    setSendingRem(true); setRemError('');
    try {
      const r = await axios.post(`${API}/fitter-returns/${id}/send-reminder`, { actor, subject: remSubject, body: remBody });
      hydrate(r.data?.return || r.data);
      onChanged?.();
    } catch (e) {
      setRemError(e.response?.data?.detail || 'Send failed.');
    }
    setSendingRem(false);
  };

  const markReceived = async () => {
    setReceiving(true); setReceivedError('');
    try {
      const r = await axios.post(`${API}/fitter-returns/${id}/received`, { actor });
      hydrate(r.data?.return || r.data);
      onChanged?.();
    } catch (e) {
      setReceivedError(e.response?.data?.detail || 'Could not mark received.');
    }
    setReceiving(false);
  };

  if (loading) return (
    <div data-testid="ret-detail">
      <button data-testid="ret-back" onClick={onBack} className="inline-flex items-center gap-1 text-xs text-hg-text-dim hover:text-hg-text font-sans"><ChevronLeft size={13} /> Back</button>
      <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-hg-green" /></div>
    </div>
  );
  if (error || !ret) return (
    <div data-testid="ret-detail" className="space-y-4">
      <button data-testid="ret-back" onClick={onBack} className="inline-flex items-center gap-1 text-xs text-hg-text-dim hover:text-hg-text font-sans"><ChevronLeft size={13} /> Back</button>
      <div className="text-xs text-red-400 font-sans">{error || 'Return not found.'}</div>
    </div>
  );

  const od = overdueLabel(ret);
  const isReceived = ret.status === 'received';
  const isLost = ret.status === 'lost';
  const closed = isClosed(ret);
  const isService = ret.return_type === 'service';
  const kit = ret.kit_checklist || [];

  return (
    <div data-testid="ret-detail" className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button data-testid="ret-back" onClick={onBack} className="inline-flex items-center gap-1 text-xs text-hg-text-dim hover:text-hg-text font-sans"><ChevronLeft size={13} /> Back</button>
        <StatusBadge status={ret.status} />
      </div>

      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="font-display text-xl text-hg-text tracking-wider flex items-center gap-2">
          <PackageCheck size={18} className="text-hg-green" /> {ret.fitter_name || '(no name)'}
        </h2>
        {ret.fitter_email && <span className="text-xs font-sans text-hg-text-dim">{ret.fitter_email}</span>}
      </div>

      {/* Overdue banner */}
      {ret.is_overdue && !closed && (
        <div className="text-sm font-sans text-red-400 bg-red-500/10 border border-red-500/30 rounded-sm px-4 py-3 flex items-start gap-2">
          <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" />
          <span>Past the 30-day return window — review. The {money(ret.nonreturn_charge_amount)} non-return charge is flagged but NEVER auto-charged; acting on it is a manual decision.</span>
        </div>
      )}

      {/* Received banner */}
      {isReceived && (
        <div className={`text-sm font-sans rounded-sm px-4 py-3 flex items-start gap-2 ${ret.billing_stop_pending ? 'text-amber-300 bg-amber-400/10 border border-amber-400/30' : 'text-hg-green bg-hg-green/10 border border-hg-green/30'}`} data-testid="ret-received-banner">
          <CheckCircle2 size={15} className="mt-0.5 flex-shrink-0" />
          <span className="flex-1">
            Received on {fmtDate(ret.received_at)}.
            {ret.billing_stop_pending
              ? ' OPEN TASK: confirm the monthly fee is stopped in billing — this stays flagged (and in the digest) until confirmed.'
              : ` Billing stop confirmed ${fmtDate(ret.billing_stop_confirmed_at)}.`}
          </span>
          {ret.billing_stop_pending && (
            <button data-testid="ret-billing-stopped" onClick={onBillingStopped}
              className="flex-shrink-0 px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90">
              Fee stopped — confirm
            </button>
          )}
        </div>
      )}

      {/* Lost banner: closed with no charge and no billing stop */}
      {isLost && (
        <div className="text-sm font-sans rounded-sm px-4 py-3 flex items-start gap-2 text-hg-text bg-hg-bg border border-hg-border" data-testid="ret-lost-banner">
          <AlertTriangle size={15} className="mt-0.5 flex-shrink-0 text-hg-text-dim" />
          <span>
            Closed as lost on {fmtDate(ret.lost_at)}{ret.lost_by ? ` by ${ret.lost_by}` : ''}: {ret.lost_reason}
            {' '}No charge and no billing stop — the fitter stays active.
          </span>
        </div>
      )}

      {/* Awaiting-send banner: the 30-day clock has NOT started */}
      {ret.status === 'awaiting_send' && (
        <div className="text-sm font-sans text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded-sm px-4 py-3 flex items-start gap-2">
          <Clock size={15} className="mt-0.5 flex-shrink-0" />
          <span>The return email hasn't been sent yet — the 30-day clock starts when you send it below.</span>
        </div>
      )}

      {/* Shipping deal + helper page link */}
      <ShippingSection ret={ret} actor={actor} onSaved={hydrate} />

      {/* Tracking */}
      {!closed && (
        <Section title="Shipment tracking">
          {ret.tracking?.number ? (
            <div className="text-sm font-sans text-hg-text">
              {ret.tracking.carrier ? `${ret.tracking.carrier} · ` : ''}{ret.tracking.number}
              <span className="text-hg-text-dim text-xs"> · added {fmtDate(ret.tracking.added_at)}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <input value={trkCarrier} onChange={(e) => setTrkCarrier(e.target.value)} placeholder="Carrier (optional)"
                className="w-36 bg-hg-bg border border-hg-border rounded-sm px-2.5 py-1.5 text-xs font-sans text-hg-text" />
              <input value={trkNumber} onChange={(e) => setTrkNumber(e.target.value)} placeholder="Tracking number"
                className="w-56 bg-hg-bg border border-hg-border rounded-sm px-2.5 py-1.5 text-xs font-sans text-hg-text"
                data-testid="ret-tracking-input" />
              <button onClick={onSaveTracking} disabled={!trkNumber.trim()}
                className="px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-green hover:border-hg-green disabled:opacity-40">
                Save tracking
              </button>
              {trkError && <span className="text-xs text-red-400">{trkError}</span>}
            </div>
          )}
        </Section>
      )}

      {/* Timeline */}
      <Section title="Timeline">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Field label="Return started" value={fmtDate(ret.return_started_at)} />
          <Field label="Return by" value={fmtDate(ret.return_by_date)} />
          <Field label={ret.is_overdue ? 'Overdue' : 'Days left'} value={od ? od.text : (closed ? 'Closed' : '—')} />
          {ret.received_at && <Field label="Received at" value={fmtDate(ret.received_at)} />}
          {ret.lost_at && <Field label="Closed as lost" value={fmtDate(ret.lost_at)} />}
        </div>
      </Section>

      {/* Kit to return */}
      <Section title="Kit to return">
        {kit.length === 0 ? (
          <div className="text-sm text-hg-text-dim font-sans">No kit items.</div>
        ) : (
          <ul className="space-y-1.5">
            {kit.map((k, i) => (
              <li key={i} className="flex items-center gap-2 text-sm font-sans">
                {k?.received
                  ? <CheckCircle2 size={14} className="text-hg-green" />
                  : <span className="inline-block w-3.5 h-3.5 border border-hg-border rounded-sm" />}
                <span className="text-hg-text">{k?.item}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Return email (draft-and-review) */}
      <Section title="Return email">
        {/* A send used to produce NO visible change: this line showed return_email_sent_at,
            so after sending a FOLLOW-UP it still displayed the original send's date and
            Randall had no way to tell whether his click had done anything. Every send is
            now acknowledged, and the whole outreach history is on the page. */}
        {sendOk && (
          <div className="text-[11px] font-sans text-hg-text bg-hg-green/15 border border-hg-green/40 rounded-sm px-2.5 py-2 flex items-center gap-1.5"
            data-testid="ret-send-ok">
            <CheckCircle2 size={12} className="text-hg-green" /> {sendOk}
          </div>
        )}
        {(ret.outreach || []).length > 0 && (
          <div className="space-y-1" data-testid="ret-outreach">
            <div className="text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">
              What {ret.fitter_name?.split(' ')[0]} has been sent
            </div>
            {(ret.outreach || []).map((t, i) => (t.out_of_band || t.kind === 'phone') ? (
              // Happened OUTSIDE the Hub, recorded after the fact. Never "awaiting
              // confirmation" — there is no campaign. Only a Sent Items copy is proof.
              <div key={i} className="text-[11px] font-sans flex items-start gap-2 border rounded-sm px-2 py-1"
                style={toneStyle('outside')} data-testid="ret-outreach-oob">
                <span>
                  <strong>{fmtDate(t.at)}</strong>
                  {' · '}{(t.kind || 'contact').replace('_', ' ')}
                  {' · '}{CHANNEL_LABEL[t.channel || 'phone']}
                  {t.to && <> · to {t.to}</>}
                  {t.cc?.length ? ` · cc ${t.cc.join(', ')}` : ''}
                  {t.subject && <> · “{t.subject}”</>}
                  {t.who && <> · {t.who}</>}
                  {' · '}{t.delivery_confirmed ? 'sent — Sent Items copy on record' : 'recorded, not sent by the Hub'}
                  {t.note && <> · {t.note}</>}
                </span>
              </div>
            ) : (
              <div key={i} className="text-[11px] font-sans flex items-start gap-2">
                {t.delivery_confirmed
                  ? <CheckCircle2 size={12} className="text-hg-green mt-0.5 flex-shrink-0" />
                  : t.kind === 'note'
                  ? <AlertTriangle size={12} className="text-hg-text-dim mt-0.5 flex-shrink-0" />
                  : <Loader2 size={12} className="text-amber-300 mt-0.5 flex-shrink-0" />}
                <span className="text-hg-text-dim">
                  <span className="text-hg-text">{fmtDate(t.at)}</span>
                  {t.kind !== 'note' && <> · {t.kind} · to {t.to}
                    {t.cc?.length ? ` · cc ${t.cc.join(', ')}` : ''}
                    {' · '}
                    {t.delivery_confirmed
                      ? <span className="text-hg-green">delivery confirmed</span>
                      : <span className="text-amber-300">awaiting confirmation — not yet proven delivered</span>}
                  </>}
                  {t.note && <> · {t.note}</>}
                </span>
              </div>
            ))}
          </div>
        )}
        {ret.return_email_sent_at && (
          <div className="text-[11px] font-sans text-hg-green flex items-center gap-1.5" data-testid="ret-email-sent">
            <CheckCircle2 size={12} /> Return email sent {fmtDate(ret.return_email_sent_at)}
            {ret.followup_sent_at && <> · follow-up sent {fmtDate(ret.followup_sent_at)}</>}
          </div>
        )}
        <div className="space-y-2">
          <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">Subject</label>
          <input
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green"
          />
          <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">Body</label>
          <textarea
            value={emailBody}
            onChange={(e) => setEmailBody(e.target.value)}
            rows={10}
            className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans leading-relaxed focus:outline-none focus:ring-1 focus:ring-hg-green resize-y"
          />
        </div>
        {emailError && <div className="text-xs text-red-400 font-sans">{emailError}</div>}
        {!closed && (
          <button
            data-testid="ret-send-email"
            onClick={sendEmail}
            disabled={sendingEmail}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40"
          >
            {sendingEmail ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} {ret.return_email_sent_at ? 'Send follow-up' : 'Send return email'}
          </button>
        )}
        {ret.return_email_sent_at && (
          <p className="text-[10px] font-sans text-hg-text-dim">
            Goes out as a follow-up: the original send on {fmtDate(ret.return_email_sent_at)} stays
            on the record, and the {ret.return_by_date ? 'return-by date' : 'deadline'} does not move.
          </p>
        )}
      </Section>

      {/* Conversation: out-of-band contact + replies captured onto this return */}
      <Section title="Conversation">
        {!closed && <LogContact ret={ret} actor={actor} onSaved={(d) => { hydrate(d); onChanged?.(); }} />}
        <ReturnThread ret={ret} actor={actor} onChanged={(d) => { if (d?.return_id) hydrate(d); onChanged?.(); }} />
      </Section>

      {/* Reminder (only if overdue) */}
      {ret.is_overdue && !closed && (
        <Section title="Reminder">
          {ret.last_overdue_notice && (Date.now() - new Date(ret.last_overdue_notice.at).getTime()) < 7 * 86400000 && (
            <div className="text-[11px] font-sans border rounded-sm px-2.5 py-2" style={toneStyle('warn')}
              data-testid="ret-recent-notice">
              An overdue notice already went out {ret.last_overdue_notice.channel === 'outlook_direct' ? 'from Outlook' : 'from the Hub'} on{' '}
              {fmtDate(ret.last_overdue_notice.at)} (“{ret.last_overdue_notice.subject}”). The Hub will refuse
              another reminder for 7 days — answer their reply above instead.
            </div>
          )}
          {ret.reminder_sent_at && (
            <div className="text-[11px] font-sans text-hg-green flex items-center gap-1.5" data-testid="ret-reminder-sent">
              <CheckCircle2 size={12} /> Sent {fmtDate(ret.reminder_sent_at)}
            </div>
          )}
          <div className="space-y-2">
            <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">Subject</label>
            <input
              value={remSubject}
              onChange={(e) => setRemSubject(e.target.value)}
              className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green"
            />
            <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">Body</label>
            <textarea
              value={remBody}
              onChange={(e) => setRemBody(e.target.value)}
              rows={8}
              className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans leading-relaxed focus:outline-none focus:ring-1 focus:ring-hg-green resize-y"
            />
          </div>
          {remError && <div className="text-xs text-red-400 font-sans">{remError}</div>}
          <button
            data-testid="ret-send-reminder"
            onClick={sendReminder}
            disabled={sendingRem}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-sans border border-hg-green text-hg-green rounded-sm hover:bg-hg-green/10 disabled:opacity-40"
          >
            {sendingRem ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} {ret.reminder_sent_at ? 'Resend reminder' : 'Send reminder'}
          </button>
        </Section>
      )}

      {/* $3,500 non-return charge (only if flagged) */}
      {ret.nonreturn_charge_flagged && (
        <Section title="Non-return charge">
          <div className="text-sm font-sans text-red-400 bg-red-500/10 border border-red-500/30 rounded-sm px-4 py-3 flex items-start gap-2">
            <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" />
            <span>Non-return charge of {money(ret.nonreturn_charge_amount)} is prepared and surfaced. Charging is a manual Stax step done outside the Hub — the Hub never auto-charges.</span>
          </div>
        </Section>
      )}

      {/* Actions */}
      {receivedError && <div className="text-xs text-red-400 font-sans">{receivedError}</div>}
      {!closed && (
        <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-3">
          <button
            data-testid="ret-received"
            onClick={markReceived}
            disabled={receiving}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40"
          >
            {receiving ? <Loader2 size={13} className="animate-spin" /> : <PackageCheck size={13} />} Mark system received
          </button>
          <p className="text-[11px] font-sans text-hg-text-dim">
            {isService
              ? 'Marking received closes this service return. The fitter stays active — nothing is billed off.'
              : 'Marking received closes this return. Remember to stop the monthly fee in billing.'}
          </p>
          {isService && <LostClose ret={ret} actor={actor} onClosed={(d) => { hydrate(d); onChanged?.(); }} />}
        </div>
      )}
    </div>
  );
}

// SERVICE returns only (the backend refuses an offboarding one): the system is gone and is not
// coming back. Two steps and a written reason, because it is a close and it is for good.
// Writes `lost` with who/when/why — no charge, no billing stop, no false "received".
function LostClose({ ret, actor, onClosed }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const ready = reason.trim().length >= 10;

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${API}/fitter-returns/${ret.return_id}/lost`, { reason: reason.trim(), actor });
      onClosed?.(r.data?.return || r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not close this return as lost.');
    }
    setBusy(false);
  };

  if (!open) {
    return (
      <button data-testid="ret-lost-open" onClick={() => setOpen(true)}
        className="text-[11px] font-sans text-hg-text-dim underline hover:text-hg-text">
        The system is lost — close this return without a charge
      </button>
    );
  }
  return (
    <div className="border border-hg-border rounded-sm p-3 space-y-2" data-testid="ret-lost-form">
      <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">What happened</label>
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
        placeholder="e.g. Lost in transit; HG is replacing it, no charge."
        data-testid="ret-lost-reason"
        className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans" />
      <p className="text-[11px] font-sans text-hg-text-dim">
        Closes the return as lost, for good. No charge is flagged and no billing stop is opened — {ret.fitter_name || 'the fitter'} stays active.
      </p>
      {err && <div className="text-xs text-red-400 font-sans">{err}</div>}
      <div className="flex gap-2">
        <button data-testid="ret-lost-confirm" onClick={submit} disabled={!ready || busy}
          className="px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text rounded-sm hover:border-hg-green disabled:opacity-40">
          {busy ? <Loader2 size={12} className="animate-spin inline" /> : 'Close as lost'}
        </button>
        <button onClick={() => { setOpen(false); setReason(''); setErr(''); }}
          className="px-3 py-1.5 text-xs font-sans text-hg-text-dim hover:text-hg-text">Cancel</button>
      </div>
    </div>
  );
}

// --- main module ---

export default function ReturnsInbox() {
  const { user } = useAuth();
  const actor = user?.email || 'admin';

  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // A dashboard row opens the exact return: ?status= picks the filter it lives under (the
  // default "In progress" hides overdue and received rows) and ?return= selects it.
  const [link] = useState(() => takeDeepLink(['status', 'return']));
  const [status, setStatus] = useState(FILTERS.includes(link.status) ? link.status : 'in_progress');
  const [selectedId, setSelectedId] = useState(link.return || null);

  const fetchReturns = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API}/fitter-returns?status=${encodeURIComponent(status)}`);
      setReturns(r.data?.returns || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load returns.');
    }
    setLoading(false);
  }, [status]);

  useEffect(() => { fetchReturns(); }, [fetchReturns]);

  if (selectedId) {
    return (
      <div className="space-y-5" data-testid="returns-page">
        <ReturnDetail
          id={selectedId}
          actor={actor}
          onBack={() => setSelectedId(null)}
          onChanged={fetchReturns}
        />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="returns-page">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider">RETURNS</h1>
        <p className="text-sm text-hg-text-dim font-sans mt-1">
          Manage the return of HG demo systems. Manual + human-in-the-loop — nothing auto-charges, emails are draft-and-review.
        </p>
      </div>

      {/* Start a return */}
      <StartReturn
        actor={actor}
        onStarted={(rid) => { fetchReturns(); if (rid) setSelectedId(rid); }}
      />

      {/* Filters */}
      <div className="bg-hg-surface border border-hg-border rounded-sm p-4">
        <div className="flex gap-1.5 flex-wrap items-center">
          {FILTERS.map((s) => (
            <button
              key={s}
              data-testid={`ret-filter-${s}`}
              onClick={() => setStatus(s)}
              className={`px-3 py-1.5 text-xs font-sans rounded-sm border transition-colors ${
                status === s ? 'border-hg-green bg-hg-green/10 text-hg-green' : 'border-hg-border text-hg-text-dim hover:border-hg-green/50'
              }`}
            >
              {s === 'all' ? 'All' : STATUS_META[s].label}
            </button>
          ))}
          <button
            onClick={fetchReturns}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green ml-auto"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {error && <div className="text-xs text-red-400 font-sans" data-testid="ret-error">{error}</div>}

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-hg-green" /></div>
      ) : returns.length === 0 ? (
        <div className="text-center py-12 text-sm text-hg-text-dim font-sans" data-testid="ret-empty">
          <FileText size={20} className="mx-auto mb-2 text-hg-text-dim/60" />
          No returns match this filter.
        </div>
      ) : (
        <div className="space-y-3" data-testid="ret-list">
          {returns.map((r) => (
            <ReturnCard key={r.return_id} ret={r} onOpen={setSelectedId} />
          ))}
        </div>
      )}
    </div>
  );
}
